import React from 'react';
import { Input } from '../../../ui/Input';
import { formatVariableName } from '../../../../utils/nameUtils';

interface ParameterConfig {
  type: string;
  label: string;
  default?: any;
  description?: string;
  required?: boolean;
  options?: string[] | Record<string, string>;
  readonly?: boolean;
}

interface ParameterInputProps {
  id: string;
  paramKey: string;
  config: ParameterConfig;
  value: any;
  hasError: boolean;
  onChange: (key: string, value: any) => void;
}

export const ParameterInput: React.FC<ParameterInputProps> = ({
  id,
  paramKey,
  config,
  value,
  hasError,
  onChange
}) => {
  const handleChange = (newValue: any) => onChange(paramKey, newValue);

  const isKeyValueOptions = typeof config.options === 'object' && !Array.isArray(config.options);
  switch (config.type) {
    case 'string':
    case 'email':
      return (
        <Input
          id={id}
          value={value || ''}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={config.description}
          className={hasError ? 'border-red-500 focus:ring-red-500' : ''}
          disabled={config.readonly}
          readOnly={config.readonly}
        />
      );
      
    case 'number':
      return (
        <Input
          id={id}
          type="number"
          value={value || ''}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={config.description}
          className={hasError ? 'border-red-500 focus:ring-red-500' : ''}
        />
      );
      
    case 'boolean':
      return (
        <label className="flex items-center space-x-2">
          <input
            id={id}
            type="checkbox"
            checked={value || false}
            onChange={(e) => handleChange(e.target.checked)}
            className={`w-4 h-4 text-blue-600 rounded focus:ring-blue-500 ${
              hasError ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          <span className="text-sm text-gray-600">{config.description}</span>
        </label>
      );
      
    case 'select':
      return (
        <select
          id={id}
          value={value || config.default}
          onChange={(e) => handleChange(e.target.value)}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            hasError ? 'border-red-500' : 'border-gray-300'
          }`}
        >
          {isKeyValueOptions 
            ? Object.entries(config.options as Record<string, string>).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))
            : (config.options as string[])?.map((option: string) => (
                <option key={option} value={option}>
                  {option.charAt(0).toUpperCase() + option.slice(1)}
                </option>
              ))
          }
        </select>
      );
      
    case 'multiselect':
      return (
        <div 
          id={id} 
          className={`space-y-2 p-2 border rounded-lg ${
            hasError ? 'border-red-500' : 'border-gray-300'
          }`}
        >
          {Array.isArray(config.options) && config.options.map((option: string) => (
            <label key={option} className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={(value || []).includes(option)}
                onChange={(e) => {
                  const currentValues = value || [];
                  const newValues = e.target.checked
                    ? [...currentValues, option]
                    : currentValues.filter((v: string) => v !== option);
                  handleChange(newValues);
                }}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">
                {formatVariableName(option)}
              </span>
            </label>
          ))}
        </div>
      );
      
    case 'text':
      return (
        <textarea
          id={id}
          value={value || ''}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={config.description}
          rows={4}
          className={`text-sm w-full px-3 py-2 border rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            hasError ? 'border-red-500' : 'border-gray-300'
          }`}
        />
      );
      
    case 'object':
      return (
        <textarea
          id={id}
          value={typeof value === 'object' ? JSON.stringify(value, null, 2) : value || '{}'}
          onChange={(e) => {
            try {
              const parsed = JSON.parse(e.target.value);
              handleChange(parsed);
            } catch {
              handleChange(e.target.value);
            }
          }}
          placeholder={config.description}
          rows={4}
          className={`w-full px-3 py-2 border rounded-lg resize-none font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            hasError ? 'border-red-500' : 'border-gray-300'
          }`}
        />
      );
      
    default:
      return (
        <Input
          id={id}
          value={value || ''}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={config.description}
          className={hasError ? 'border-red-500 focus:ring-red-500' : ''}
        />
      );
  }
};